import React, { createContext, useContext, useState, useEffect } from "react";
import { matchPath } from "react-router-dom";
import { augmentCookieName, getCookie } from "./cookies.js";
import * as Sentry from "@sentry/browser";
import AxiosFactory from "./axiosFactory.js";
import sharedConfig from "./config.js";
import { isMetaAuthRequest } from "./utils/index.js";

const LoginSessionContext = createContext();

/**
 * Downloads/syncs/manages the user session data (user/org info needed for the front end apps).
 * Caches data in memory and saves to local storage.
 * It stores a login session version with the data and re-downloads it when it's out of sync with the session id cookie from the server.
 * This allows the server to trigger an update on all front end apps just by attaching a new session version cookie in any response.
 * Checks the version's as soon as a consuming component re-renders/mounts, throttled to once every 500ms, and checking takes less then 1 ms.
 * also checks after every response from the server incase the server has updated the session version.
 *
 * note:
 *  - the data is not stored in a cookie directly because it's too large to fit.
 *  - methods prefixed with __ are not meant to be used outside of this file, and are only public so the context provider can use them.
 *  - the version is not used to validate/identify the user so it does not need to be secure, the request for data is secured with the JWT cookie like anything else.
 */
class LoginSession {
    #_axios = undefined; //lazy init to avoid circular dependency
    #throttling = false;
    #listeners = new Set();
    #dataVersion;
    orgInfo;
    userInfo;
    isStale = true;
    authExemptFrontendRoutes = new Set();

    //lazy getter for axios instance
    get #axios() {
        if (!this.#_axios) {
            this.#_axios = new AxiosFactory({
                baseURL: sharedConfig.centralBackendURL,
                withCredentials: true,
            }).create("api");
        }
        return this.#_axios;
    }

    constructor() {
        const jsonData = localStorage.getItem(augmentCookieName("login_session_data"));
        const data = jsonData ? JSON.parse(jsonData) : {};
        this.#setDataWeak(data.userInfo, data.orgInfo, data.dataVersion);
    }

    __subscribe(listener) {
        this.#listeners.add(listener);
        return () => this.#listeners.delete(listener);
    }

    // sets data without updating local storage or triggering listeners
    #setDataWeak(userInfo, orgInfo, dataVersion) {
        this.isStale = dataVersion !== getCookie(augmentCookieName("login_session_data_version"));
        this.userInfo = userInfo;
        this.orgInfo = orgInfo;
        this.#dataVersion = dataVersion;

        /**
         * freeze in dev mode to detect erroneous mutations
         * (this object is sent directly to consumers without copying for performance reasons, so it would be bad if they mutate it)
         */
        if (process.env.REACT_APP_NODE_ENV in ["development", "devserver", "test"]) {
            Object.freeze(this.userInfo);
            Object.freeze(this.orgInfo);
        }

        // Set sentry user for error reporting
        Sentry.setUser({ username: userInfo?.username });
    }

    /**
     * Sets the session data, updates the cache and local storage, and triggers listeners.
     *
     * @param {Object} userInfo - User specific login session info.
     * @param {Object} orgInfo - Organization-wide login session info for current user.
     */
    #setData(userInfo, orgInfo) {
        this.#setDataWeak(
            userInfo,
            orgInfo,
            userInfo ? getCookie(augmentCookieName("login_session_data_version")) : undefined,
        );
        localStorage.setItem(
            augmentCookieName("login_session_data"),
            JSON.stringify({
                userInfo,
                orgInfo,
                dataVersion: this.#dataVersion,
            }),
        );
        this.isStale = false;
        this.#listeners.forEach((listener) => listener());
    }

    /**
     * Checks if the user is logged in and updates the session data if it's out of sync.
     * @returns {boolean} True if there is a session data version(logged in), false otherwise(not logged in).
     */
    __checkAndUpdateData() {
        const sessionId = getCookie(augmentCookieName("login_session_data_version"));

        console.debug(
            "checking login session data",
            "version from cookie:",
            sessionId,
            "version from data:",
            this.#dataVersion,
        );

        if (!sessionId) {
            return false;
        }

        this.isStale = this.#dataVersion !== sessionId;

        //if out of sync reload from server
        if (this.isStale) {
            this.#axios
                .get("login_session_data")
                .then((response) => {
                    this.#setData(response.data.user, response.data.org_info);
                })
                .catch((error) => {
                    console.log(error);
                });
        }

        return true;
    }

    __throttledCheckAndUpdateData() {
        //keeping this method extra simple since it will be called a lot and we want it inlined
        if (this.#throttling) {
            return;
        }

        // allow again in 500ms
        this.#throttling = true;
        setTimeout(() => {
            this.#throttling = false;
        }, 500);

        if (
            !this.__checkAndUpdateData() &&
            //don't force them to be logged in if they are on a route that doesn't require it,
            //(but still allow them to use these routes if they are logged in)
            !this.isFrontendRouteExempt(window.location.pathname)
        ) {
            //is not/should not be logged in
            console.debug("logging out due to no session data");
            console.trace();
            this.logout(false);
        }
    }

    /**
     * Removes login state/info from local storage, resets Sentry context, and removes JWT cookies.
     * By default also redirects the user to the login page.
     * Optionally overrides post login page, mostly for redirecting to same page after user re-auths.
     *
     * @param {boolean} [gotoLoginPage=true] - Determines if the user should be redirected to the login page after logout.
     * @param {string|URL} [postReloginRedirectURL=undefined] - The URL to redirect the user to after they log back in.
     * This can be a string or a `URL` object. If provided, this URL will be passed as a query parameter to the login page.
     * Ignored if !gotoLoginPage.
     *
     * @returns {Promise<void>}
     */
    async logout(gotoLoginPage = true, postReloginRedirectURL = undefined) {
        console.debug("logging out", "redirect:", gotoLoginPage, "postRedirect:", postReloginRedirectURL);
        console.trace();

        // Unset cookies
        document.cookie = `${augmentCookieName("login_session_data_version")}=; Path=/; max-age=0; Domain=${sharedConfig.rootDomain};`;
        document.cookie = `${augmentCookieName("sentry_session_id")}=; Path=/; max-age=0; Domain=${sharedConfig.rootDomain};`;

        // Clear session data
        this.#setData(undefined, undefined);

        // JWTs are stored in httponly cookies, so we need to call the backend to send a request to delete them
        this.#axios.post("auth/logout").catch((error) => {
            if (error.response && error.response.status >= 400 && error.response.status < 500) {
                // ignore 400 series errors, just means the user was already logged out
            } else {
                // Rethrow or handle other kinds of errors
                return Promise.reject(error);
            }
        });

        // redirect to login page
        if (gotoLoginPage) {
            let redirectURL = sharedConfig.centralFrontendURL;
            if (postReloginRedirectURL) {
                // Append the postRelogginRedirectURL as a query parameter
                redirectURL += `?redirect=${encodeURIComponent(postReloginRedirectURL)}`;
            }
            window.location.href = redirectURL;
        }
    }

    /**
     * @typedef {Object} RouteObject
     * @property {string} path - The path pattern to match.
     * @property {boolean} [exact] - If true, only matches when the path is exactly the same as the pattern.
     * @property {boolean} [strict] - If true, it will be case sensitive.
     */

    /**
     * Exempts frontend routes from requiring login session data.
     * The routes should be in the same format used for the first parameter of the `react-router-dom` `matchPath` function.
     * They can be either strings or objects with `path`, `exact`, and `strict` keys.
     *
     * @example
     * // Exempting FHC end-user survey routes
     * loginSession.addRouteExemptions("/survey/*");
     *
     * @example
     * // Using a route object
     * loginSession.addRouteExemptions({ path: "/survey/*", strict: true, exact: false });
     *
     * @example
     * // Exempting multiple routes
     * loginSession.addRouteExemptions("/survey/*", "/some-route", { path: "/another-route", exact: true });
     *
     * @param {...(string | { path: string, exact?: boolean, strict?: boolean })} routes - The routes to exempt.
     */
    addRouteExemptions(...routes) {
        routes.forEach((route) => {
            try {
                // check if the route/routePattern is valid
                matchPath(route, "/");
                // Add the route to the set of exempt routes if it's valid
                this.authExemptFrontendRoutes.add(route);
            } catch (e) {
                console.error("invalid route pattern (ignored):", route, e);
            }
        });
    }

    /**
     * Checks if a path corresponds to a frontend route that's exempt from requiring login session data.
     * @param {string} path The path to check.
     * @returns {boolean} True if exempt, false otherwise.
     */
    isFrontendRouteExempt(path) {
        return Array.from(this.authExemptFrontendRoutes).some((x) => matchPath(x, path));
    }
}

const loginSession = new LoginSession();

export { loginSession };

/**
 * Attaches an interceptor that automatically updates the session data when the server sends a new session id.
 * @param {import("axios").AxiosInstance} axios To attach interceptor to.
 */
export function attachLoginSessionInterceptor(axios) {
    axios.interceptors.response.use(
        (response) => {
            if (
                loginSession.userInfo && // only if logged in (according to this session(tab usually))
                !loginSession.__checkAndUpdateData() // only if it indicated we aren't logged in anymore
            ) {
                //the loginSession thought it was logged in but we aren't (probably logged out by the user in another tab/browser)
                if (!isMetaAuthRequest(response.config)) {
                    //don't ensure logout if we are doing login/logout state stuff, also this would inf loop for logout route.
                    loginSession.logout();
                }
            }
            return response;
        },
        (error) => {
            return Promise.reject(error);
        },
    );
}

/**
 * React Context Provider that allows using the LoginSession class with React components.
 *
 * It makes login session data available to all child components through React Context, and
 * automatically re-renders when session data changes, causing all consuming components to
 * re-render with the updated data. It eliminates the need for prop drilling and ensures all
 * components stay synchronized with the current authentication state.
 *
 * The provider subscribes to the LoginSession's internal change notifications and uses a force
 * update pattern to trigger re-renders throughout the component tree when session data changes.
 *
 * IMPORTANT NOTE ON FORCE UPDATE PATTERN:
 *  - It looks like a hacky solution that could be replaced with the built-in `useSyncExternalStore` React hook.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components that will have access to login session context.
 *
 * @example
 * // Wrap your app with the provider
 * <LoginSessionProvider>
 *   <Routes>
 *      ...
 *   </Routes>
 * </LoginSessionProvider>
 *
 * @example
 * // Get the login session data in a component
 * function MyComponent() {
 *   const { userInfo, orgInfo, logout, isStale } = useLoginSession();
 *   ...
 * }
 */
export const LoginSessionProvider = ({ children }) => {
    // Force update counter - incrementing this value triggers re-renders of all consuming components
    const [_, forceUpdate] = useState(0);

    useEffect(() => {
        // Subscribe to changes in LoginSession when the component mounts.
        // This ensures the provider re-renders whenever session data changes (login/logout/refresh).
        const unsubscribe = loginSession.__subscribe(() => {
            forceUpdate(_ + 1);
        });

        // Unsubscribe when the component unmounts to prevent memory leaks
        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LoginSessionContext.Provider
            // Need a new object so that the context value changes when the login session data changes
            value={{
                userInfo: loginSession.userInfo,
                orgInfo: loginSession.orgInfo,
                // Allow consumers to call logout directly without having to import loginSession
                logout: loginSession.logout.bind(loginSession),
                isStale: loginSession.isStale,
            }}
        >
            {children}
        </LoginSessionContext.Provider>
    );
};

/**
 * A hook to get the login session data.
 * @param {boolean} redirectIfNoSession - If true, the user will be logged out and redirected to the login page if there is no login session.
 * @returns {Object} The login session data.
 */
export const useLoginSession = (redirectIfNoSession = true) => {
    const context = useContext(LoginSessionContext);

    //avoiding useEffect here, since we are already throttling
    //also don't want to wait till end of render to start potential network request
    loginSession.__throttledCheckAndUpdateData();

    const loggedIn = !!getCookie(augmentCookieName("login_session_data_version"));
    if (redirectIfNoSession && !loggedIn && !loginSession.isFrontendRouteExempt(window.location.pathname)) {
        //__throttledCheckAndUpdateData may have logged us out but not redirected, if another
        //caller passed redirectIfNoSession=false, so we need to redirect here.
        loginSession.logout(true);
    }

    return context;
};
